import { useState, useEffect, useContext } from "react";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material"

import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { formatCurrency } 		from "../../../common/Helper";
import CompanyContext 			from "../context";

import Loading 					from "../../../components/Loading";
import QuarterResultsDialog 	from "./DialogQuarterResults";
import useAuth from "../../../hooks/useAuth";
import RestrictedAccess from "../../../components/RestrictedAccess";
import { Link } from "react-router-dom";

const TabFinancialReports = () => {	

	const {company, currency, exchangeRate}				= useContext(CompanyContext)
	const {auth}										= useAuth()

	const [isLoading, setIsLoading]						= useState(true)
	const [financialReports, setFinancialReports] 		= useState()
	const [selectedYear, setSelectedYear]				= useState()
	const [isQuarterDialogOpen, setIsQuarterDialogOpen] = useState(false);
	const [showInDollar, setShowInDollar]				= useState(false)

	useEffect(() => {
		if (company) {
			setFinancialReports(calculateFinancialData(company.financialReports, 10))
			setIsLoading(false)
		}
	}, [company])	

	const calculateFinancialData = (pData, pLimit) => {
		return pData.slice(0, pLimit + 1).map(d => {
			return {
				...d,
				grossProfit 	: d.revenue - d.cost,
				grossMargin		: (d.revenue - d.cost)/d.revenue,
				netDebt			: d.debt - d.cash,
				netMargin		: d.netIncome/d.revenue,
				debtToEquity	: d.debt/d.equity,
				debtToEbitda	: (d.debt - d.cash)/d.ebitda,
				fcfCapex		: d.fco + d.capex,
				capexToOcf		: Math.abs(d.capex) / d.fco
			}
		})
	}

	const handleQuarterResultsDialogOpen = (pYear) => {
		setIsQuarterDialogOpen(true)
		setSelectedYear(pYear)
	}

	if (isLoading) return <Loading/>

	return (
		<Box>
			<Box display={'flex'} flexDirection={'row'}>
				<Box minWidth={400} width={auth.userId ? '100%' : null}>
				<TableContainer component={Paper}>
					<QuarterResultsDialog 
						open={isQuarterDialogOpen} 
						selectedYear={selectedYear} 
						setOpen={setIsQuarterDialogOpen} 
						showInDollar={showInDollar} 
						setShowInDollar={setShowInDollar} 
					/>
					<Table size="small" >
						<TableHead>
							<TableRow>
								<TableCell align="left">Indicador</TableCell>
								{ financialReports && financialReports.map((fr, index) => (
									<TableCell 
										key={fr.year}
										align="center" 
										onClick={index < 10 ? () => handleQuarterResultsDialogOpen(fr.year) : null}
										sx={{cursor: index < 10 ? 'pointer' : '' }}
										>
										{fr.year}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Receita" format="$,0" item="revenue"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Custo" format="$,0" item="cost" smallerIsBetter/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Lucro Bruto" format="$,0" item="grossProfit"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="EBITDA" format="$,0" item="ebitda"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="D&A" format="$,0" item="dea" smallerIsBetter/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Rec. Financeiras" format="$,0" item="netInterestIncome"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Lucro Líquido" format="$,0" item="netIncome"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Lucro por Ação" format="$,2" item="eps"/>					
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Margem Líquida" format="%,2" item="netMargin"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Patr. Líquido" format="$,0" item="equity"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Valor Patr. por Ação" format="$,2" item="bookValue"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Caixa" format="$,0" item="cash" />
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Dívida" format="$,0" item="debt" smallerIsBetter/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Dívida Líquida" format="$,0" item="netDebt" smallerIsBetter/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Dívida/PL" format="#,2" item="debtToEquity" smallerIsBetter/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Dívida Líq./EBITDA" format="#,2" item="debtToEbitda" smallerIsBetter/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="FCO" format="$,0" item="fco"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="CAPEX" format="$,0" item="capex"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="FCL CAPEX" format="$,0" item="fcfCapex"/>
							<FinancialRow data={financialReports} showInDollar={showInDollar} exchangeRate={exchangeRate} label="CAPEX/FCO" format="%,2" item="capexToOcf" smallerIsBetter/>
						</TableBody>
					</Table>
				</TableContainer>
				</Box>
				
				{!auth.userId ? (
					<Box display={'flex'} flexGrow={1} width={'100%'} justifyContent={'center'} alignItems={'center'}>
						<RestrictedAccess message={'Faça login para visualizar os Dados Financeiros dos anos anteriores.'}/>
					</Box>
				) : null }

			</Box>
			<Box display={'flex'} marginTop={1}  justifyContent={'right'}>
				<Typography variant="caption">
					*Valores em milhões de {showInDollar ? 'USD (cotação de hoje)' : currency}. 
				</Typography>
				{ currency !== 'USD' ? (
					<Typography component={Link} variant="caption" paddingLeft={0.5} sx={{ cursor : 'pointer'}} onClick={() => setShowInDollar(!showInDollar)}>
						{ showInDollar ? ' Mostrar em '+ currency : ' Converter para USD utilizando a cotação de hoje. ' }
					</Typography>
				) : null }
			</Box>
		</Box>
	
	)
}

const FinancialRow = (props) => {

	let financialCells 		= []
	let smallerIsBetter		= false
	let showInDollar		= props.showInDollar
	let currentExchangeRate	= props.exchangeRate
	let currentCurrency		= props.data[0].currency

	if (props.smallerIsBetter) smallerIsBetter = props.smallerIsBetter

	// Cria um novo array 
	// 1) Convertendo os valores para dolar, se especificado;
	// 2) Calculando as variações ano a ano
	for (let i=0;i<props.data.length;i++) {
		financialCells.push({
			[props.item] 	: props.data[i][props.item],
			variation 		: i < props.data.length-1 && props.data[i+1][props.item] !== 0 ? ((props.data[i][props.item] - props.data[i+1][props.item]) / Math.abs(props.data[i+1][props.item])) * 100: 0
		})
	}

	const formatValue = (pValue, pFormat) => {
		if (isNaN(pValue) || pValue === Infinity) return 'N/A'
		switch (pFormat) {
			case '$,0' : return formatCurrency(showInDollar ? pValue * currentExchangeRate : pValue, 'BIG', showInDollar ? 'USD' : currentCurrency)
			case '$,2' : return formatCurrency(showInDollar ? pValue * currentExchangeRate : pValue, 'SMALL', showInDollar ? 'USD' : currentCurrency)
			case '%,2' : return (pValue * 100).toFixed(2) +'%'
			case '#,2' : return pValue.toFixed(2)
		}
	}

	return (
		<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
			<TableCell component="th" scope="row">{props.label}</TableCell>
			{ financialCells && financialCells.map((fr, index) => (
				<TableCell align="center" key={props.label + index}>
					<Box justifyContent={'center'} alignItems={'center'} display={'flex'}>
						<Typography variant="body2">{formatValue(fr[props.item], props.format)}</Typography>
						{ fr.variation && fr.variation !== 0 ? (
							<Tooltip title={fr.variation.toFixed(2) +'%'}>
								{ (fr.variation >= 0 ? <ArrowDropUpIcon style={{ color: smallerIsBetter ? 'red' : 'green' }} /> : <ArrowDropDownIcon style={{ color: smallerIsBetter ? 'green' : 'red' }} />) }	
							</Tooltip>
						): null }
					</Box>
				</TableCell>
			)) }
		</TableRow>
	)
}

export default TabFinancialReports